import type { Melody, MelodyEntityNote } from '@tunasong/schemas'
import { SUGGESTION_VELOCITY } from './convert.js'
import { GRID_TOLERANCE, gridSlotBeats, maxOffsetMs, snapToSlot } from './grid.js'

/**
 * The edit operations the piano roll performs, as pure functions on the melody.
 *
 * The roll is a view over these: every drag, stretch and click resolves to one call here, so the
 * §2 invariants (grid-aligned onsets, ordered, non-overlapping, bounded offsets) are enforced in
 * one tested place instead of in pointer handlers.
 *
 * THE OVERLAP RULE (§2, §4.1): a melody is a single voice, so a note landing inside an earlier
 * note's span SHORTENS the earlier note to end where the new one begins. A note landing exactly
 * on another's onset replaces it - there is no room left to shorten. Growing a note is likewise
 * capped at the next note's onset.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §2, §4.1
 */

const clamp = (value: number, low: number, high: number): number => Math.min(high, Math.max(low, value))

const byOnset = (a: MelodyEntityNote, b: MelodyEntityNote): number => a.onset - b.onset

/**
 * Re-establish the single-voice invariant around `note`, which is already in `notes`.
 * Earlier notes reaching into it are trimmed; a note starting at the same onset is dropped; and
 * `note` itself is trimmed so it does not reach into its successor.
 */
const resolveOverlaps = (notes: MelodyEntityNote[], note: MelodyEntityNote): MelodyEntityNote[] => {
  const kept = notes.filter(n => n === note || Math.abs(n.onset - note.onset) > GRID_TOLERANCE)
  const index = kept.indexOf(note)

  return kept.map((n, i) => {
    if (i === index) {
      const next = kept[i + 1]
      return next ? { ...n, duration: Math.min(n.duration, next.onset - n.onset) } : n
    }
    if (i === index - 1 && n.onset + n.duration > note.onset + GRID_TOLERANCE) {
      return { ...n, duration: note.onset - n.onset }
    }
    return n
  })
}

const withNotes = (melody: Melody, notes: MelodyEntityNote[]): Melody => ({ ...melody, notes })

export interface InsertMelodyNoteInput {
  midi: number
  onset: number
  /** Beats. Defaults to one grid slot. */
  duration?: number
  velocity?: number
}

export const insertMelodyNote = (melody: Melody, input: InsertMelodyNoteInput): Melody => {
  const slot = gridSlotBeats(melody.grid)
  const note: MelodyEntityNote = {
    midi: clamp(Math.round(input.midi), 0, 127),
    onset: snapToSlot(input.onset, melody.grid),
    duration: Math.max(slot, snapToSlot(input.duration ?? slot, melody.grid)),
    velocity: clamp(input.velocity ?? SUGGESTION_VELOCITY, 0, 1),
    offsetMs: 0,
  }
  return withNotes(melody, resolveOverlaps([...melody.notes, note].sort(byOnset), note))
}

export const moveMelodyNote = (melody: Melody, index: number, to: { midi: number; onset: number }): Melody => {
  const existing = melody.notes[index]
  if (!existing) {
    return melody
  }
  const moved: MelodyEntityNote = {
    ...existing,
    midi: clamp(Math.round(to.midi), 0, 127),
    onset: snapToSlot(to.onset, melody.grid),
  }
  const rest = melody.notes.filter((_, i) => i !== index)
  return withNotes(melody, resolveOverlaps([...rest, moved].sort(byOnset), moved))
}

export const resizeMelodyNote = (melody: Melody, index: number, durationBeats: number): Melody => {
  const existing = melody.notes[index]
  if (!existing) {
    return melody
  }
  const slot = gridSlotBeats(melody.grid)
  const next = melody.notes[index + 1]
  const wanted = Math.max(slot, snapToSlot(durationBeats, melody.grid))
  const capped = next ? Math.min(wanted, next.onset - existing.onset) : wanted
  return withNotes(
    melody,
    melody.notes.map((n, i) => (i === index ? { ...n, duration: capped } : n))
  )
}

export const setMelodyNoteOffset = (melody: Melody, index: number, offsetMs: number): Melody => {
  const bound = maxOffsetMs(melody.tempo, melody.grid)
  return withNotes(
    melody,
    melody.notes.map((n, i) => (i === index ? { ...n, offsetMs: clamp(offsetMs, -bound, bound) } : n))
  )
}

export const setMelodyNoteVelocity = (melody: Melody, index: number, velocity: number): Melody =>
  withNotes(
    melody,
    melody.notes.map((n, i) => (i === index ? { ...n, velocity: clamp(velocity, 0, 1) } : n))
  )

export const removeMelodyNote = (melody: Melody, index: number): Melody =>
  withNotes(
    melody,
    melody.notes.filter((_, i) => i !== index)
  )
